import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { Store } from "lucide-react";

/**
 * Per-user restaurant scope picker. Shows restaurants from the selected markets
 * grouped by market. Leave empty to grant the user access to ALL restaurants in
 * their assigned markets (default behavior).
 *
 * Props:
 *   - selectedUnits:  array of market IDs the user has been assigned to
 *   - selected:       array of currently selected restaurant names
 *   - onChange(arr):  called whenever the selection changes
 */
export default function RestaurantPicker({ selectedUnits = [], selected = [], onChange }) {
  const [byMarket, setByMarket] = useState({}); // { marketId: { fullName, restaurants: [...] } }
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!selectedUnits.length) {
      setByMarket({});
      return;
    }
    setLoading(true);
    try {
      const results = await Promise.all(
        selectedUnits.map(async (m) => {
          try {
            const res = await api.get(`/markets/${encodeURIComponent(m)}/restaurants`);
            return [m, res.data.restaurants || []];
          } catch {
            return [m, []];
          }
        })
      );
      const next = {};
      results.forEach(([m, rs]) => { next[m] = rs; });
      setByMarket(next);
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(selectedUnits)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggle = (name) => {
    const set = new Set(selected);
    if (set.has(name)) set.delete(name); else set.add(name);
    onChange(Array.from(set));
  };

  const selectAll = (marketRestaurants) => {
    const set = new Set(selected);
    marketRestaurants.forEach((r) => set.add(r.name || r));
    onChange(Array.from(set));
  };

  const clearAll = (marketRestaurants) => {
    const names = new Set(marketRestaurants.map((r) => r.name || r));
    onChange(selected.filter((n) => !names.has(n)));
  };

  if (!selectedUnits.length) {
    return (
      <p className="text-xs text-gray-500 italic">
        Select at least one Business Unit first.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-gray-500">
        Optional. Leaving everything unchecked gives the user access to <em>all</em>
        {" "}restaurants in their assigned markets. Selecting specific restaurants
        will scope every ticket they create or see to those locations.
      </p>
      {loading && <p className="text-xs text-gray-400">Loading restaurants…</p>}
      {Object.entries(byMarket).map(([marketId, restaurants]) => {
        if (!restaurants.length) {
          return (
            <div key={marketId} className="rounded-md border border-dashed border-gray-200 p-2">
              <p className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">{marketId}</span> — no
                restaurants configured. Add them in <em>Markets → Restaurants</em>.
              </p>
            </div>
          );
        }
        const selectedInMarket = restaurants.filter((r) =>
          selected.includes(r.name || r)
        ).length;
        return (
          <div
            key={marketId}
            className="rounded-md border border-gray-200 bg-white"
            data-testid={`restaurant-group-${marketId}`}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2 text-sm">
                <Store className="w-3.5 h-3.5 text-indigo-600" />
                <span className="font-semibold text-gray-800">{marketId}</span>
                <span className="text-[11px] text-gray-500">
                  {selectedInMarket}/{restaurants.length} selected
                </span>
              </div>
              <div className="flex items-center gap-1 text-[11px]">
                <button
                  type="button"
                  onClick={() => selectAll(restaurants)}
                  className="px-1.5 py-0.5 rounded hover:bg-indigo-50 text-indigo-700"
                >
                  All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={() => clearAll(restaurants)}
                  className="px-1.5 py-0.5 rounded hover:bg-gray-100 text-gray-600"
                >
                  None
                </button>
              </div>
            </div>
            <div className="px-3 py-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
              {restaurants.map((r) => {
                const name = r.name || r;
                const checked = selected.includes(name);
                return (
                  <label
                    key={name}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer ${
                      checked ? "bg-indigo-50 text-indigo-900" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(name)}
                      className="h-3.5 w-3.5 accent-indigo-600"
                      data-testid={`restaurant-checkbox-${marketId}-${name}`}
                    />
                    <span className="truncate">{name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
